import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import useApi from '../../hooks/useApi'
import useAlert from '../../hooks/useAlert'
import runLogic from '../../../../common/logic/runLogic'
import runFieldsLogic from '../../../../common/logic/runFieldsLogic'
import { arrayMove } from '@dnd-kit/sortable'
import generateEndpoint from '../../utils/generateEndpoint'

export default function useGridListActions(config, dataHookResult) {
    const { rows: sourceRows, mode, setState, state, fullState, setFullState, routeId } = dataHookResult
    const { postMe, putMe, deleteMe } = useApi()
    const { AlertComponent, showAlert } = useAlert()
    const queryClient = useQueryClient()

    const [dirtyRows, setDirtyRows] = useState(new Set())
    const [savedRows, setSavedRows] = useState(new Set())
    const savedTimeoutRef = useRef(null)

    useEffect(() => {
        return () => { if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current) }
    }, [])

    const linesArray = config.linesArray
    const storePath = config.storePath || []

    const [localRows, setLocalRows] = useState([])
    const sourceRowsRef = useRef(sourceRows)
    const hasLocalEdits = useRef(false)

    /*
     * Inline, the store is the record: every edit here writes it, so its lines
     * are always what the grid shows — and something beside the grid can write
     * them too (a line's documents, from the side panel). Following the store
     * keeps the next cell edit from writing back a row that lacks them.
     * Collection mode keeps its unsaved rows until they are saved.
     */
    useEffect(() => {
        sourceRowsRef.current = sourceRows
        if (mode === 'inline' || !hasLocalEdits.current) {
            setLocalRows(sourceRows)
        }
    }, [sourceRows, mode])

    const effectiveRows = localRows

    const markDirty = useCallback((rowId) => {
        setDirtyRows(prev => {
            const next = new Set(prev)
            next.add(rowId)
            return next
        })
        setSavedRows(prev => {
            if (!prev.has(rowId)) return prev
            const next = new Set(prev)
            next.delete(rowId)
            return next
        })
    }, [])

    const clearDirty = useCallback(() => {
        setDirtyRows(new Set())
        hasLocalEdits.current = false
    }, [])

    const addItem = useCallback(() => {
        const newRow = {}
        if (config.defaultData) {
            config.defaultData.forEach(item => {
                if (item.defaultType === 'value') {
                    newRow[item.id] = item.defaultValue
                }
            })
        }

        if (mode === 'inline') {
            const currentLines = Array.isArray(state?.[linesArray]) ? state[linesArray] : []
            const newLines = [...currentLines, newRow]
            setState({ [linesArray]: newLines, savedOn: new Date() }, { path: storePath })
            hasLocalEdits.current = true
            setLocalRows(prev => [...prev, newRow])
        } else if (mode === 'collection') {
            const parentIdField = config.parentIdField
            const parentRow = parentIdField && routeId ? { [parentIdField]: routeId } : {}
            const withNew = [...sourceRowsRef.current, { ...newRow, ...parentRow, _new: true }]
            hasLocalEdits.current = true
            setLocalRows(withNew)
            markDirty(withNew.length - 1)
        }
    }, [mode, state, linesArray, setState, storePath, config, markDirty])

    const deleteItem = useCallback((idx) => {
        if (mode === 'inline') {
            const condition = state[linesArray]?.[idx]?.[config.meta?.preventDelete?.field]
            if (config.meta?.preventDelete && condition > 0) {
                showAlert('inventory.shipment.line.prevent.delete.title', 'inventory.shipment.line.prevent.delete.body', 'warning')
                return
            }

            const currentLines = state[linesArray] || []
            const newLines = currentLines.filter((_, i) => i !== idx)
            const newData = { ...state, [linesArray]: newLines }
            const processedData = runLogic(newData, state.entity)
            setState({ ...processedData, savedOn: new Date() }, { path: storePath })
            setLocalRows(prev => prev.filter((_, i) => i !== idx))

            setDirtyRows(prev => {
                const next = new Set()
                prev.forEach(id => {
                    if (id < idx) next.add(id)
                    else if (id > idx) next.add(id - 1)
                })
                return next
            })
        } else if (mode === 'collection') {
            const item = localRows[idx]
            if (item?._new) {
                setLocalRows(prev => prev.filter((_, i) => i !== idx))
                setDirtyRows(prev => {
                    const next = new Set()
                    prev.forEach(id => {
                        if (id < idx) next.add(id)
                        else if (id > idx) next.add(id - 1)
                    })
                    return next
                })
            } else if (item?.id) {
                const deleteEndpoint = config.endpoint && routeId
                    ? generateEndpoint(config.endpoint, routeId)
                    : config.entity
                deleteMe(deleteEndpoint, item.id).then(() => {
                    queryClient.invalidateQueries({ queryKey: ['list', deleteEndpoint] })
                })
                setLocalRows(prev => prev.filter((_, i) => i !== idx))
                setDirtyRows(prev => {
                    const next = new Set()
                    prev.forEach(id => {
                        if (id < idx) next.add(id)
                        else if (id > idx) next.add(id - 1)
                    })
                    return next
                })
            }
        }
    }, [mode, state, linesArray, setState, storePath, config, localRows, deleteMe, showAlert])

    const saveAll = useCallback(async () => {
        const justSaved = new Set(dirtyRows)
        if (mode === 'inline') {
            const processedData = runLogic(fullState, fullState.entity)
            setFullState({ ...processedData, savedOn: new Date() })
            if (config.persistParent && config.parentEntity) {
                const parentId = processedData.id || routeId
                await putMe(config.parentEntity, processedData, parentId)
            }
            clearDirty()
        } else if (mode === 'collection') {
            const newItems = []
            const existingItems = []
            for (const rowIdx of dirtyRows) {
                const item = localRows[rowIdx]
                if (!item) continue
                if (item._new) {
                    const { _new, ...data } = item
                    newItems.push(data)
                } else if (item.id) {
                    existingItems.push(item)
                }
            }
            const saveEndpoint = config.endpoint && routeId
                ? generateEndpoint(config.endpoint, routeId)
                : config.entity
            for (const item of newItems) await postMe(saveEndpoint, item)
            if (existingItems.length > 0) await postMe(`${saveEndpoint}/batch`, { items: existingItems })
            queryClient.invalidateQueries({ queryKey: ['list', saveEndpoint] })
            clearDirty()
        }
        if (justSaved.size > 0) {
            if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current)
            setSavedRows(justSaved)
            savedTimeoutRef.current = setTimeout(() => setSavedRows(new Set()), 5000)
        }
    }, [mode, fullState, setFullState, config, localRows, dirtyRows, postMe, putMe, routeId, clearDirty])

    const fillAll = useCallback((sourceField, targetField) => {
        hasLocalEdits.current = true
        const newRows = localRows.map(row => ({ ...row, [targetField]: row[sourceField] ?? 0 }))
        setLocalRows(newRows)
        setDirtyRows(new Set(newRows.map((_, i) => i)))

        if (mode === 'inline') {
            const currentLines = state[linesArray] || []
            const newLines = currentLines.map(row => ({ ...row, [targetField]: row[sourceField] ?? 0 }))
            const newData = { ...state, [linesArray]: newLines }
            const processedData = runLogic(newData, state.entity)
            setState({ ...processedData, savedOn: new Date() }, { path: storePath })
        }
    }, [mode, localRows, state, linesArray, setState, storePath])

    const reorderItems = useCallback((oldIndex, newIndex) => {
        if (mode !== 'inline') return
        const currentLines = state[linesArray] || []
        const reordered = arrayMove(currentLines, oldIndex, newIndex)
        setState({ [linesArray]: reordered, savedOn: new Date() }, { path: storePath })
        setLocalRows(prev => arrayMove(prev, oldIndex, newIndex))
    }, [mode, state, linesArray, setState, storePath])

    const updateCellValue = useCallback(async (rowIdx, fieldOrUpdates, value) => {
        hasLocalEdits.current = true
        markDirty(rowIdx)

        const currentRow = localRows[rowIdx] || {}
        const prevRow = { ...currentRow }

        // Support multi-field: updateCellValue(rowIdx, { field1: val1, field2: val2 })
        const isMulti = value === undefined && typeof fieldOrUpdates === 'object'
        const updates = isMulti ? fieldOrUpdates : { [fieldOrUpdates]: value }
        const primaryField = Object.keys(updates)[0]

        let updatedRow = { ...currentRow, ...updates }

        if (config.entity) {
            const processedRow = await runFieldsLogic(updatedRow, config.entity, [primaryField], prevRow)
            updatedRow = processedRow
        }

        if (mode === 'inline') {
            const currentLines = state[linesArray] || []
            const newLines = [...currentLines]
            newLines[rowIdx] = updatedRow
            const newData = { ...state, [linesArray]: newLines }
            const processedData = runLogic(newData, state.entity)
            setState({ ...processedData, savedOn: new Date() }, { path: storePath })
            setLocalRows(Array.isArray(processedData[linesArray]) ? processedData[linesArray] : [])
        } else {
            setLocalRows(prev => {
                const next = [...prev]
                next[rowIdx] = updatedRow
                return next
            })
        }
    }, [mode, state, linesArray, setState, storePath, markDirty, config, localRows])

    return {
        effectiveRows,
        addItem,
        deleteItem,
        saveAll,
        fillAll,
        reorderItems,
        dirtyRows,
        savedRows,
        markDirty,
        clearDirty,
        updateCellValue,
        AlertComponent,
    }
}
